import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  AlertCircle,
  MapPin,
  Mail,
  Phone,
  Users,
  Sprout,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  Heart,
  Shield,
  Database,
  ChevronDown,
  Check,
  Clock,
} from "lucide-react";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import {
  Card,
  Badge,
  Progress,
  formatDate,
} from "../adminComponents/adminComponents";
import {
  formatUGX,
  STAGE_LABELS,
  STAGE_LEVELS,
  SECTOR_LABELS,
  GENDER_LABELS,
} from "../../data/mockData";
import { useAdminVentureHook } from "../../hooks/useAdminHooks";
import { useManagerVentureHook } from "../../hooks/useManagerHooks";
import { useAdminVentureDueDiligenceHook } from "../../hooks/useAdminHooks";
import { useManagerVentureDueDiligenceHook } from "../../hooks/useManagerHooks";
import "./ventureDetail.css";

const MILESTONE_LABELS = {
  first_revenue: "First Revenue",
  breakeven: "Break-even",
  external_funding: "External Funding",
  ten_employees: "10+ Employees",
  product_market_fit: "Product-Market Fit",
  scale: "Scale Expansion",
};

const STATUS_META = {
  active: { label: "Active", tone: "success" },
  graduated: { label: "Graduated", tone: "accent" },
  inactive: { label: "Inactive", tone: "muted" },
};

const REPAYMENT_META = {
  on_track: { label: "On Track", tone: "success" },
  completed: { label: "Completed", tone: "accent" },
  behind: { label: "Behind", tone: "danger" },
  not_applicable: { label: "Not Applicable", tone: "muted" },
};

const READINESS_META = {
  ready_advanced: { label: "Advanced", tone: "danger" },
  ready: { label: "Investment Ready", tone: "primary" },
  developing: { label: "Developing", tone: "accent" },
  early: { label: "Early Stage", tone: "warning" },
  not_ready: { label: "Not Ready", tone: "muted" },
};

function getRiskLevel(score) {
  if (score >= 80) return "low";
  if (score >= 55) return "medium";
  return "high";
}

function getRiskMeta(level) {
  if (level === "low") return { label: "Low", tone: "success", score: 82 };
  if (level === "average")
    return { label: "Average", tone: "warning", score: 58 };
  if (level === "medium")
    return { label: "Medium", tone: "warning", score: 58 };
  return { label: "High", tone: "danger", score: 36 };
}

function getReadiness(performanceScore) {
  if (performanceScore >= 85) return "ready_advanced";
  if (performanceScore >= 72) return "ready";
  if (performanceScore >= 58) return "developing";
  if (performanceScore >= 40) return "early";
  return "not_ready";
}

function getPhaseOverallScore(rankings) {
  if (!rankings || typeof rankings !== "object") return null;
  const values = Object.values(rankings)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  if (values.length === 0) return null;
  const averageOutOfFive =
    values.reduce((sum, value) => sum + value, 0) / values.length;
  return Math.round((averageOutOfFive / 5) * 100);
}

function formatDaysAgo(dateStr) {
  if (!dateStr) return "Never";
  const days = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / 86400000,
  );
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

function mapApiToVenture(data) {
  if (!data) return null;
  const { venture, owner, contacts, location, classification, employment } =
    data;
  const ownerGender = String(owner?.gender ?? "")
    .trim()
    .toLowerCase();
  const stageRaw = String(venture?.level ?? "").trim();
  const stage = stageRaw ? stageRaw.toLowerCase() : null;
  const sector =
    classification?.thematic_areas?.[0] || classification?.sectors?.[0] || null;
  const geography =
    location?.admin_level_2 || location?.admin_level_1 || location?.country;
  let cohort = null;
  if (venture?.start_date) {
    const d = new Date(venture.start_date);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const quarter = Math.ceil((d.getMonth() + 1) / 3);
      cohort = `${year}-Q${quarter}`;
    }
  }
  return {
    id: venture?.id,
    name: venture?.name,
    status: venture?.business_status,
    description: venture?.value_proposition,
    sector,
    founderName: owner?.full_name,
    founderEmail: contacts?.emails?.[0],
    founderPhone: contacts?.phones?.[0]?.number,
    geography,
    cohort,
    teamSize: (() => {
      const direct = employment?.totals?.direct?.total;
      const indirect = employment?.totals?.indirect?.total;
      if (direct == null && indirect == null) return null;
      return Number(direct ?? 0) + Number(indirect ?? 0);
    })(),
    stage,
    stageRaw,
    milestones: [],
    lastDataUpdate: venture?.updated_at ?? venture?.formalized_at,
    dataCompleteness: null,
    primaryProduct: venture?.value_proposition,
    beneficiariesReached: null,
    genderClassification:
      ownerGender && ownerGender !== "male" ? "women_led" : null,
  };
}

function mapApiToMetrics(data) {
  if (!data) return null;

  const finance = data?.finance ?? {};
  const accumulative = finance?.accumulative ?? {};
  const periods = finance?.periods ?? {};
  const employmentTotals = data?.employment?.totals ?? {};

  return {
    revenue: Number(accumulative?.revenue ?? 0),
    revenue3m: Number(periods?.revenue_3m ?? 0),
    revenue6m: Number(periods?.revenue_6m ?? 0),
    revenue12m: Number(periods?.revenue_12m ?? 0),
    revenueGrowth3m:
      periods?.revenue_growth_3m == null
        ? null
        : Number(periods.revenue_growth_3m),
    revenueGrowth6m:
      periods?.revenue_growth_6m == null
        ? null
        : Number(periods.revenue_growth_6m),
    revenueGrowth12m:
      periods?.revenue_growth_12m == null
        ? null
        : Number(periods.revenue_growth_12m),
    capitalReceived: Number(accumulative?.finance_mobilized ?? 0),
    externalCapital: 0,
    repaymentStatus: "not_applicable",
    jobsFullTime: Number(employmentTotals?.full_time?.total ?? 0),
    jobsPartTime: Number(employmentTotals?.part_time?.total ?? 0),
  };
}

export default function VentureDetail() {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [ddExpanded, setDdExpanded] = useState(false);
  const isAdmin = user?.role === ROLES.ADMIN;
  const isManager = user?.role === ROLES.MANAGER;

  const workspaceBase = user?.role === ROLES.ADMIN ? "/admin" : "/manager";

  const adminVentureQuery = useAdminVentureHook({
    token,
    ventureId: id,
    enabled: isAdmin && !!id,
  });

  const managerVentureQuery = useManagerVentureHook({
    token,
    ventureId: id,
    enabled: isManager && !!id,
  });

  const adminDueDiligenceQuery = useAdminVentureDueDiligenceHook({
    token,
    ventureId: id,
    enabled: isAdmin && !!id,
  });

  const managerDueDiligenceQuery = useManagerVentureDueDiligenceHook({
    token,
    ventureId: id,
    enabled: isManager && !!id,
  });

  const dueDiligenceApiData = isManager
    ? managerDueDiligenceQuery.data
    : adminDueDiligenceQuery.data;

  const apiData = isManager ? managerVentureQuery.data : adminVentureQuery.data;
  const isLoading = isManager
    ? managerVentureQuery.isLoading
    : adminVentureQuery.isLoading;
  const isError = isManager
    ? managerVentureQuery.isError
    : adminVentureQuery.isError;

  const venture = useMemo(() => mapApiToVenture(apiData), [apiData]);
  const dueDiligence = apiData?.due_diligence ?? {
    scores: {
      overall_score: 0,
      performance_score: 0,
      behavioral_score: 0,
      risk: { level: "average", value: 0 },
      investment_readiness: { key: null, label: null },
    },
    weighted_summary: {
      problem_market_fit: 0.0,
      business_model: 0.0,
      growth_plan: 0.0,
      capacity_support: 0.0,
      dependency_risk: 0.0,
      behavioral_assessment: {
        motivation: 0.0,
        integrity: 0.0,
        coachability: 0.0,
        adaptability: 0.0,
        partnership: 0.0,
      },
      assessment_notes: null,
    },
  };
  const diligenceRow = apiData?.diligence_row ?? {
    performanceScore: 0,
    riskLevel: null,
  };
  const metrics = useMemo(() => mapApiToMetrics(apiData), [apiData]);
  const surveys = [];

  const firstSurvey = surveys[0];
  const latestSurvey = surveys[surveys.length - 1];

  const qolRadarData = useMemo(() => {
    if (!firstSurvey || !latestSurvey) return [];
    const baseline = Math.min(
      100,
      Math.round((firstSurvey.qualityOfLifeScore || 0) * 20),
    );
    const current = Math.min(
      100,
      Math.round((latestSurvey.qualityOfLifeScore || 0) * 20),
    );
    return [
      {
        indicator: "Income",
        baseline: Math.max(0, baseline - 8),
        current: Math.min(100, current + 4),
      },
      { indicator: "Food Security", baseline, current },
      {
        indicator: "Energy",
        baseline: Math.max(0, baseline - 12),
        current: Math.min(100, current + 2),
      },
      {
        indicator: "Wellbeing",
        baseline: Math.max(0, baseline - 4),
        current: Math.min(100, current + 6),
      },
    ];
  }, [firstSurvey, latestSurvey]);

  const milestones = useMemo(() => {
    const known = Object.entries(MILESTONE_LABELS).map(([key, label]) => ({
      key,
      label,
      achieved: venture?.milestones?.includes(key),
    }));
    const extras = (venture?.milestones || [])
      .filter((milestone) => !(milestone in MILESTONE_LABELS))
      .map((milestone) => ({
        key: milestone,
        label: milestone
          .replaceAll("_", " ")
          .replace(/\b\w/g, (char) => char.toUpperCase()),
        achieved: true,
      }));
    return [...known, ...extras];
  }, [venture?.milestones]);

  const dueDiligenceStages = useMemo(() => {
    const rankings = dueDiligenceApiData?.rankings;
    if (!rankings || typeof rankings !== "object") return [];
    return Object.entries(rankings).map(([stageKey, entries]) => ({
      key: stageKey,
      label: stageKey
        .replaceAll("_", " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
      rows:
        entries && typeof entries === "object"
          ? Object.entries(entries).map(([fieldKey, rawValue]) => ({
              key: fieldKey,
              label: fieldKey
                .replaceAll("_", " ")
                .replace(/\b\w/g, (char) => char.toUpperCase()),
              value: Number(rawValue ?? 0),
            }))
          : [],
    }));
  }, [dueDiligenceApiData]);

  if (isLoading) {
    return (
      <div className="adminVentureDetail-notFound">
        <p className="admin-muted">Loading venture&hellip;</p>
      </div>
    );
  }

  if (isError || !venture) {
    return (
      <div className="adminVentureDetail-notFound">
        <AlertCircle className="adminVentureDetail-notFoundIcon" />
        <h2 className="adminVentureDetail-notFoundTitle">
          {isError ? "Failed to load venture" : "Venture not found"}
        </h2>
        <Link
          to={`${workspaceBase}/ventures`}
          className="admin-btn admin-btn-primary"
        >
          <ArrowLeft className="admin-btn-icon" />
          Back to Ventures
        </Link>
      </div>
    );
  }

  const stageLevel = STAGE_LEVELS[venture?.stage] ?? 1;
  const readinessScores =
    dueDiligence?.scores ?? dueDiligence?.readiness_scores ?? {};
  const weightedSummary = dueDiligence?.weighted_summary ?? {};
  const behavioralAssessment = weightedSummary?.behavioral_assessment ?? {};
  const assessmentNotes =
    weightedSummary?.assessment_notes ?? weightedSummary?.assessement_notes;

  const performanceScore =
    readinessScores?.performance_score ??
    dueDiligence?.performanceScore ??
    diligenceRow?.performanceScore ??
    0;
  const behavioralScore =
    readinessScores?.behavioral_score ??
    dueDiligence?.behavioralScore ??
    Math.round(performanceScore * 0.88);
  const overallWeightedScore =
    readinessScores?.overall_score ??
    dueDiligence?.overallWeightedScore ??
    Math.round(performanceScore * 0.7 + behavioralScore * 0.3);
  const riskPayload = readinessScores?.risk ?? {};
  const riskValue = Number(riskPayload?.value ?? 0);
  const riskLevel =
    riskPayload?.level ??
    dueDiligence?.riskLevel ??
    diligenceRow?.riskLevel ??
    getRiskLevel(performanceScore);
  const riskMeta = getRiskMeta(riskLevel);
  const riskDisplayScore =
    riskPayload?.value === null || riskPayload?.value === undefined
      ? riskMeta.score
      : riskValue;
  const readinessKey =
    readinessScores?.investment_readiness?.key ??
    dueDiligence?.investmentReadiness ??
    getReadiness(performanceScore);
  const readinessLabel =
    readinessScores?.investment_readiness?.label ??
    READINESS_META[readinessKey]?.label ??
    "Not Ready";
  const readinessTone = READINESS_META[readinessKey]?.tone ?? "muted";
  // Prefer backend-computed weighted scores when available, fall back to the
  // simple phase average (via getPhaseOverallScore) otherwise. `null` from the
  // backend means the phase is incomplete and its ring should be hidden.
  const performance = dueDiligenceApiData?.performance ?? null;
  const ideationOverallScore =
    performance?.ideation?.performance_score ??
    getPhaseOverallScore(dueDiligenceApiData?.rankings?.ideation);
  const postIdeationOverallScore =
    performance?.post_ideation?.performance_score_no_finance ??
    getPhaseOverallScore(dueDiligenceApiData?.rankings?.post_ideation);
  const hasIdeationScore =
    ideationOverallScore !== null && ideationOverallScore !== undefined;
  const hasPostIdeationScore =
    postIdeationOverallScore !== null && postIdeationOverallScore !== undefined;
  const hasAnyPhaseScore = hasIdeationScore || hasPostIdeationScore;

  // Per-phase derived values reuse the same formulas as the single fallback:
  //   behavioral = round(performance * 0.88)
  //   risk       = getRiskLevel(performance)
  //   readiness  = getReadiness(performance)
  // When a phase has no score the derived value is null so the sub-row is hidden.
  const derivePhase = (score) => {
    if (score === null || score === undefined) return null;
    const numeric = Number(score);
    if (!Number.isFinite(numeric)) return null;
    const behavioral = Math.round(numeric * 0.88);
    const level = getRiskLevel(numeric);
    return {
      performance: numeric,
      behavioral,
      riskLevel: level,
      riskMeta: getRiskMeta(level),
      readinessKey: getReadiness(numeric),
    };
  };
  const ideationPhase = hasIdeationScore
    ? derivePhase(ideationOverallScore)
    : null;
  const postIdeationPhase = hasPostIdeationScore
    ? derivePhase(postIdeationOverallScore)
    : null;

  const problemMarketFit = Number(weightedSummary?.problem_market_fit ?? 0);
  const businessModel = Number(weightedSummary?.business_model ?? 0);
  const growthPlan = Number(weightedSummary?.growth_plan ?? 0);
  const capacitySupport = Number(weightedSummary?.capacity_support ?? 0);
  const dependencyRisk = Number(weightedSummary?.dependency_risk ?? 0);
  const motivation = Number(behavioralAssessment?.motivation ?? 0);
  const integrity = Number(behavioralAssessment?.integrity ?? 0);
  const coachability = Number(behavioralAssessment?.coachability ?? 0);
  const adaptability = Number(behavioralAssessment?.adaptability ?? 0);
  const partnership = Number(behavioralAssessment?.partnership ?? 0);

  const qolImprovement =
    firstSurvey && latestSurvey && firstSurvey.qualityOfLifeScore > 0
      ? Math.round(
          ((latestSurvey.qualityOfLifeScore - firstSurvey.qualityOfLifeScore) /
            firstSurvey.qualityOfLifeScore) *
            100,
        )
      : null;

  return (
    <div className="adminVentureDetail">
      <div className="adminVentureDetail-nav">
        <Link
          to={`${workspaceBase}/ventures`}
          className="adminVentureDetail-navBack"
        >
          <ArrowLeft size={16} />
          Back to Ventures
        </Link>
        {/* <button type="button" className="admin-btn admin-btn-primary">
          <Pencil className="admin-btn-icon" />
          Edit Venture
        </button> */}
      </div>

      <section className="adminVentureDetail-snapshot">
        <div className="adminVentureDetail-snapshotTop">
          <div className="adminVentureDetail-snapshotIntro">
            <div className="adminVentureDetail-nameRow">
              <h1 className="adminVentureDetail-name">{venture.name}</h1>
              <Badge
                tone={STATUS_META[venture.status]?.tone ?? "muted"}
                className={`adminVentureDetail-statusBadge adminVentureDetail-statusBadge-${venture.status}`}
              >
                {STATUS_META[venture.status]?.label ?? venture.status}
              </Badge>
            </div>
            <p className="adminVentureDetail-introText">
              {venture.description || "No description has been added yet."}
            </p>
          </div>
          <div className="adminVentureDetail-snapshotTags">
            <Badge tone="outline">
              {SECTOR_LABELS[venture.sector] ?? venture.sector ?? "-"}
            </Badge>
            {/* <Badge tone="outline">
              {GENDER_LABELS[venture.genderClassification] ?? "-"}
            </Badge> */}
          </div>
        </div>

        <div className="admin-grid admin-grid-4 adminVentureDetail-snapshotBody">
          <InfoItem
            icon={Mail}
            label="Founder"
            value={venture.founderName}
            subValue={venture.founderEmail || "No email on file"}
          />
          <InfoItem
            icon={MapPin}
            label="Location & Cohort"
            value={venture.geography}
            subValue={`Cohort: ${venture.cohort}`}
          />
          <InfoItem
            icon={Users}
            label="Team"
            value={venture.teamSize ?? "—"}
            subValue="members"
          />

          <div className="adminVentureDetail-stageBlock">
            <p className="adminVentureDetail-label">Graduation Stage</p>
            <div className="adminVentureDetail-stageBadgeRow">
              <Sprout size={16} />
              <Badge tone="primary">
                Level {stageLevel}:{" "}
                {STAGE_LABELS[venture.stage] ?? venture.stageRaw ?? "-"}
              </Badge>
            </div>
            <div className="adminVentureDetail-stageBar">
              {[1, 2, 3, 4, 5].map((level) => (
                <span
                  key={level}
                  className={
                    "adminVentureDetail-stageDot" +
                    (level <= stageLevel
                      ? " adminVentureDetail-stageDot-active"
                      : "")
                  }
                />
              ))}
            </div>
          </div>
        </div>

        {venture.founderPhone && (
          <div className="adminVentureDetail-contactRow admin-muted">
            <Phone size={15} />
            {venture.founderPhone}
          </div>
        )}
      </section>

      <Card
        title={
          <span className="adminVentureDetail-cardTitle">
            <Shield size={18} />
            Performance & Readiness Scores
          </span>
        }
      >
        <div className="admin-grid admin-grid-5">
          <div className="adminVentureDetail-scorePanel adminVentureDetail-scorePanel-primary">
            <p className="adminVentureDetail-label">Overall Score</p>
            {hasIdeationScore || hasPostIdeationScore ? (
              <>
                <p className="admin-muted adminVentureDetail-scoreMeta">
                  {hasIdeationScore && hasPostIdeationScore
                    ? "Post-ideation vs ideation"
                    : hasPostIdeationScore
                      ? "Post-ideation"
                      : "Ideation"}
                </p>
                <div className="adminVentureDetail-ringGroup">
                  {hasPostIdeationScore && (
                    <ScoreRing
                      label="Post-ideation"
                      value={postIdeationOverallScore}
                      variant="post"
                    />
                  )}
                  {hasIdeationScore && (
                    <ScoreRing
                      label="Ideation"
                      value={ideationOverallScore}
                      variant="ideation"
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="adminVentureDetail-ringWrap">
                <svg viewBox="0 0 100 100" className="adminVentureDetail-ring">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    className="adminVentureDetail-ringTrack"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    className="adminVentureDetail-ringValue"
                    style={{
                      strokeDasharray: `${(overallWeightedScore / 100) * 251} 251`,
                    }}
                  />
                </svg>
                <span className="adminVentureDetail-ringText">
                  {overallWeightedScore}
                </span>
              </div>
            )}
          </div>

          <ScorePanel
            label="Performance"
            value={performanceScore}
            ideation={ideationPhase?.performance}
            postIdeation={postIdeationPhase?.performance}
            splitActive={hasAnyPhaseScore}
          />
          <ScorePanel
            label="Behavioral"
            value={behavioralScore}
            ideation={ideationPhase?.behavioral}
            postIdeation={postIdeationPhase?.behavioral}
            splitActive={hasAnyPhaseScore}
          />
          <div className="adminVentureDetail-scorePanel">
            <p className="adminVentureDetail-label">Risk Level</p>
            {hasAnyPhaseScore ? (
              <div className="adminVentureDetail-phaseStack">
                {postIdeationPhase && (
                  <div className="adminVentureDetail-phaseRow">
                    <span className="admin-muted adminVentureDetail-scoreMeta">
                      Post - Ideation
                    </span>
                    <Badge tone={postIdeationPhase.riskMeta.tone}>
                      {postIdeationPhase.riskMeta.label}
                    </Badge>
                    <span className="admin-muted adminVentureDetail-scoreMeta">
                      Score: {Math.round(postIdeationPhase.performance)}
                    </span>
                  </div>
                )}
                {ideationPhase && (
                  <div className="adminVentureDetail-phaseRow">
                    <span className="admin-muted adminVentureDetail-scoreMeta">
                      Ideation
                    </span>
                    <Badge tone={ideationPhase.riskMeta.tone}>
                      {ideationPhase.riskMeta.label}
                    </Badge>
                    <span className="admin-muted adminVentureDetail-scoreMeta">
                      Score: {Math.round(ideationPhase.performance)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* <Badge tone={riskMeta.tone}>{riskMeta.label}</Badge> */}
                <p className="admin-muted adminVentureDetail-scoreMeta">
                  Score: {riskDisplayScore}
                </p>
              </>
            )}
          </div>
          <div className="adminVentureDetail-scorePanel">
            <p className="adminVentureDetail-label">Investment Readiness</p>
            {hasAnyPhaseScore ? (
              <div className="adminVentureDetail-phaseStack">
                {postIdeationPhase && (
                  <div className="adminVentureDetail-phaseRow">
                    <span className="admin-muted adminVentureDetail-scoreMeta">
                      Post - Ideation
                    </span>
                    <Badge
                      tone={
                        READINESS_META[postIdeationPhase.readinessKey]?.tone ??
                        "muted"
                      }
                    >
                      {READINESS_META[postIdeationPhase.readinessKey]?.label ??
                        "Not Ready"}
                    </Badge>
                  </div>
                )}
                {ideationPhase && (
                  <div className="adminVentureDetail-phaseRow">
                    <span className="admin-muted adminVentureDetail-scoreMeta">
                      Ideation
                    </span>
                    <Badge
                      tone={
                        READINESS_META[ideationPhase.readinessKey]?.tone ??
                        "muted"
                      }
                    >
                      {READINESS_META[ideationPhase.readinessKey]?.label ??
                        "Not Ready"}
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <Badge tone={readinessTone}>{readinessLabel}</Badge>
            )}
          </div>
        </div>
      </Card>

      <div className="admin-grid admin-grid-2">
        <Card
          title={
            <span className="adminVentureDetail-cardTitle">
              <DollarSign size={18} />
              Financial Performance
            </span>
          }
        >
          <>
            <div className="adminVentureDetail-highlight adminVentureDetail-highlight-primary">
              <span className="adminVentureDetail-highlightLabel">
                Total Revenue
              </span>
              <strong className="adminVentureDetail-highlightValue">
                {formatUGX(metrics?.revenue ?? 0)}
              </strong>
            </div>

            <div className="admin-grid admin-grid-3">
              <ScoreBlock
                label={`3-Month (${formatUGX(metrics?.revenue3m ?? 0)})`}
                value={
                  metrics?.revenueGrowth3m == null
                    ? "—"
                    : `${metrics.revenueGrowth3m >= 0 ? "+" : ""}${metrics.revenueGrowth3m}%`
                }
                tone={
                  metrics?.revenueGrowth3m == null
                    ? "muted"
                    : metrics.revenueGrowth3m >= 0
                      ? "success"
                      : "danger"
                }
                icon={
                  metrics?.revenueGrowth3m == null
                    ? null
                    : metrics.revenueGrowth3m >= 0
                      ? TrendingUp
                      : TrendingDown
                }
              />
              <ScoreBlock
                label={`6-Month (${formatUGX(metrics?.revenue6m ?? 0)})`}
                value={
                  metrics?.revenueGrowth6m == null
                    ? "—"
                    : `${metrics.revenueGrowth6m >= 0 ? "+" : ""}${metrics.revenueGrowth6m}%`
                }
                tone={
                  metrics?.revenueGrowth6m == null
                    ? "muted"
                    : metrics.revenueGrowth6m >= 0
                      ? "success"
                      : "danger"
                }
                icon={
                  metrics?.revenueGrowth6m == null
                    ? null
                    : metrics.revenueGrowth6m >= 0
                      ? TrendingUp
                      : TrendingDown
                }
              />
              <ScoreBlock
                label={`12-Month (${formatUGX(metrics?.revenue12m ?? 0)})`}
                value={
                  metrics?.revenueGrowth12m == null
                    ? "—"
                    : `${metrics.revenueGrowth12m >= 0 ? "+" : ""}${metrics.revenueGrowth12m}%`
                }
                tone={
                  metrics?.revenueGrowth12m == null
                    ? "muted"
                    : metrics.revenueGrowth12m >= 0
                      ? "success"
                      : "danger"
                }
                icon={
                  metrics?.revenueGrowth12m == null
                    ? null
                    : metrics.revenueGrowth12m >= 0
                      ? TrendingUp
                      : TrendingDown
                }
              />
            </div>

            <div className="adminVentureDetail-divider" />

            <div className="admin-grid admin-grid-2">
              <ScoreBlock
                label="Her Capital"
                value={formatUGX(metrics?.capitalReceived ?? 0)}
              />
              <ScoreBlock
                label="External Capital"
                value={formatUGX(metrics?.externalCapital ?? 0)}
              />
            </div>
          </>
        </Card>

        <Card
          title={
            <span className="adminVentureDetail-cardTitle">
              <Briefcase size={18} />
              Jobs & Enterprise Growth
            </span>
          }
        >
          <>
            <div className="admin-grid admin-grid-3">
              <StatTile label="Full-Time" value={metrics?.jobsFullTime ?? 0} />
              <StatTile label="Part-Time" value={metrics?.jobsPartTime ?? 0} />
              <StatTile
                label="Total"
                value={
                  (metrics?.jobsFullTime ?? 0) + (metrics?.jobsPartTime ?? 0)
                }
                tone="success"
              />
            </div>

            <div className="adminVentureDetail-divider" />

            <div className="adminVentureDetail-milestones">
              {milestones.map((item) => (
                <div
                  key={item.key}
                  className={
                    "adminVentureDetail-milestone" +
                    (item.achieved
                      ? " adminVentureDetail-milestone-achieved"
                      : "")
                  }
                >
                  {item.achieved ? (
                    <Check
                      size={16}
                      className="adminVentureDetail-milestoneIcon"
                    />
                  ) : (
                    <span className="adminVentureDetail-milestoneCircle" />
                  )}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </>
        </Card>
      </div>

      <Card
        title={
          <span className="adminVentureDetail-cardTitle">
            <Heart size={18} />
            Impact & Outcomes
          </span>
        }
      >
        <div className="admin-grid admin-grid-2">
          <div className="admin-stack">
            <div className="adminVentureDetail-highlight adminVentureDetail-highlight-muted">
              <span className="adminVentureDetail-highlightLabel">
                Primary Product/Service
              </span>
              <strong className="adminVentureDetail-highlightValue">
                {venture.primaryProduct || "Not specified"}
              </strong>
            </div>
            <div className="adminVentureDetail-highlight adminVentureDetail-highlight-primary">
              <span className="adminVentureDetail-highlightLabel">
                End Customers Reached
              </span>
              <strong className="adminVentureDetail-highlightValue">
                {venture.beneficiariesReached?.toLocaleString() || 0}
              </strong>
            </div>
            <div className="adminVentureDetail-impactCard">
              <span className="adminVentureDetail-highlightLabel">
                QoL Improvement
              </span>
              <strong
                className={
                  (qolImprovement ?? 0) >= 0
                    ? "admin-text-success"
                    : "admin-text-danger"
                }
              >
                {qolImprovement !== null
                  ? `${qolImprovement >= 0 ? "+" : ""}${qolImprovement}%`
                  : "0%"}
              </strong>
              <span className="admin-muted adminVentureDetail-impactMeta">
                From baseline to latest survey
              </span>
            </div>
          </div>

          <div className="adminVentureDetail-radarWrap">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart
                data={
                  qolRadarData.length > 0
                    ? qolRadarData
                    : [
                        { indicator: "Income", baseline: 0, current: 0 },
                        { indicator: "Food Security", baseline: 0, current: 0 },
                        { indicator: "Energy", baseline: 0, current: 0 },
                        { indicator: "Wellbeing", baseline: 0, current: 0 },
                      ]
                }
              >
                <PolarGrid />
                <PolarAngleAxis dataKey="indicator" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar
                  name="Baseline"
                  dataKey="baseline"
                  stroke="hsl(327, 72%, 42%)"
                  fill="hsl(327, 72%, 42%)"
                  fillOpacity={0.25}
                />
                <Radar
                  name="Current"
                  dataKey="current"
                  stroke="hsl(181, 57%, 42%)"
                  fill="hsl(181, 57%, 42%)"
                  fillOpacity={0.25}
                />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card
        title={
          <button
            type="button"
            className="adminVentureDetail-collapseHead"
            onClick={() => setDdExpanded((prev) => !prev)}
          >
            <span className="adminVentureDetail-cardTitle">
              <Shield size={18} />
              Due Diligence Summary
            </span>
            <ChevronDown
              size={18}
              className={
                "adminVentureDetail-expandIcon" +
                (ddExpanded ? " adminVentureDetail-expandIcon-open" : "")
              }
            />
          </button>
        }
        description="Click to expand full assessment details"
      >
        {dueDiligenceStages.length > 0 ? (
          <>
            {dueDiligenceStages.map((stage) => (
              <div key={stage.key}>
                <p className="adminVentureDetail-subsectionTitle">
                  {stage.label} Rankings
                </p>
                <div className="admin-grid admin-grid-5 adminVentureDetail-progressGrid">
                  {stage.rows.map((row) => (
                    <ProgressRow
                      key={row.key}
                      label={row.label}
                      value={row.value}
                      tone="primary"
                      scale={5}
                    />
                  ))}
                </div>
              </div>
            ))}
            {dueDiligenceApiData?.updated_at && (
              <p className="admin-muted adminVentureDetail-dataSub">
                Last updated: {formatDate(dueDiligenceApiData.updated_at)}
              </p>
            )}
          </>
        ) : (
          <p className="admin-muted">
            No due diligence scoring available for this venture yet.
          </p>
        )}

        {ddExpanded && (
          <>
            <div className="adminVentureDetail-divider" />
            <p className="adminVentureDetail-subsectionTitle">
              Behavioral Assessment
            </p>
            <div className="admin-grid admin-grid-5">
              <ProgressRow
                label="Motivation"
                value={motivation}
                tone="success"
                scale={5}
              />
              <ProgressRow
                label="Integrity"
                value={integrity}
                tone="success"
                scale={5}
              />
              <ProgressRow
                label="Coachability"
                value={coachability}
                tone="accent"
                scale={5}
              />
              <ProgressRow
                label="Adaptability"
                value={adaptability}
                tone="accent"
                scale={5}
              />
              <ProgressRow
                label="Partnership"
                value={partnership}
                tone="primary"
                scale={5}
              />
            </div>

            {assessmentNotes && assessmentNotes !== "null" && (
              <>
                <div className="adminVentureDetail-divider" />
                <p className="adminVentureDetail-subsectionTitle">
                  Assessment Notes
                </p>
                <p className="admin-muted">{assessmentNotes}</p>
              </>
            )}
          </>
        )}
      </Card>

      <Card
        title={
          <span className="adminVentureDetail-cardTitle">
            <Database size={18} />
            Data & Engagement
          </span>
        }
      >
        <div className="admin-grid admin-grid-3">
          <div className="adminVentureDetail-dataCol">
            <p className="adminVentureDetail-label">Last Data Update</p>
            <p className="adminVentureDetail-dataValue">
              <Clock size={15} />
              {formatDaysAgo(venture.lastDataUpdate)}
            </p>
            {venture.lastDataUpdate && (
              <p className="admin-muted adminVentureDetail-dataSub">
                {formatDate(venture.lastDataUpdate)}
              </p>
            )}
          </div>

          <div className="adminVentureDetail-dataCol">
            <p className="adminVentureDetail-label">Data Completeness</p>
            <p className="adminVentureDetail-dataPercent">
              {venture.dataCompleteness || 0}%
            </p>
            <Progress
              value={venture.dataCompleteness || 0}
              tone={
                (venture.dataCompleteness || 0) >= 80
                  ? "success"
                  : (venture.dataCompleteness || 0) >= 50
                    ? "accent"
                    : "warning"
              }
            />
          </div>

          <div className="adminVentureDetail-dataCol">
            <p className="adminVentureDetail-label">Data Status</p>
            <Badge
              tone={
                (venture.dataCompleteness || 0) >= 80
                  ? "success"
                  : (venture.dataCompleteness || 0) >= 50
                    ? "accent"
                    : "warning"
              }
            >
              {(venture.dataCompleteness || 0) >= 80
                ? "Complete"
                : (venture.dataCompleteness || 0) >= 50
                  ? "Partial"
                  : "Needs Attention"}
            </Badge>
          </div>
        </div>
      </Card>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, subValue }) {
  return (
    <div className="adminVentureDetail-infoItem">
      <div className="adminVentureDetail-infoHead">
        <Icon size={16} className="admin-muted" />
        <span className="adminVentureDetail-label">{label}</span>
      </div>
      <strong className="adminVentureDetail-infoValue">{value}</strong>
      {subValue && (
        <span className="admin-muted adminVentureDetail-infoSub">
          {subValue}
        </span>
      )}
    </div>
  );
}

function StatTile({ label, value, tone = "" }) {
  return (
    <div className="adminVentureDetail-statTile">
      <span className="adminVentureDetail-label">{label}</span>
      <strong className={tone === "success" ? "admin-text-success" : ""}>
        {value}
      </strong>
    </div>
  );
}

function ScoreRing({ label, value, variant = "post" }) {
  const numeric = Number(value);
  const hasValue = Number.isFinite(numeric);
  const bounded = hasValue ? Math.max(0, Math.min(100, numeric)) : 0;

  return (
    <div className="adminVentureDetail-ringItem">
      <div className="adminVentureDetail-ringWrap">
        <svg viewBox="0 0 100 100" className="adminVentureDetail-ring">
          <circle
            cx="50"
            cy="50"
            r="40"
            className="adminVentureDetail-ringTrack"
          />
          <circle
            cx="50"
            cy="50"
            r="40"
            className={
              "adminVentureDetail-ringValue" +
              (variant === "ideation"
                ? " adminVentureDetail-ringValue-ideation"
                : " adminVentureDetail-ringValue-post")
            }
            style={{
              strokeDasharray: `${(bounded / 100) * 251} 251`,
            }}
          />
        </svg>
        <span className="adminVentureDetail-ringText">
          {hasValue ? Math.round(bounded) : "—"}
        </span>
      </div>
      <span className="admin-muted adminVentureDetail-ringLabel">{label}</span>
    </div>
  );
}

function ScorePanel({ label, value, ideation, postIdeation, splitActive }) {
  const hasPost = postIdeation !== null && postIdeation !== undefined;
  const hasIdeation = ideation !== null && ideation !== undefined;

  if (splitActive && (hasPost || hasIdeation)) {
    return (
      <div className="adminVentureDetail-scorePanel">
        <p className="adminVentureDetail-label">{label}</p>
        <div className="adminVentureDetail-phaseStack">
          {hasPost && (
            <div className="adminVentureDetail-phaseRow">
              <span className="admin-muted adminVentureDetail-scoreMeta">
                Post - Ideation
              </span>
              <p className="adminVentureDetail-scoreBig">
                {Math.round(postIdeation)}
              </p>
              <Progress
                value={Math.max(0, Math.min(100, Number(postIdeation)))}
                tone="primary"
              />
            </div>
          )}
          {hasIdeation && (
            <div className="adminVentureDetail-phaseRow">
              <span className="admin-muted adminVentureDetail-scoreMeta">
                Ideation
              </span>
              <p className="adminVentureDetail-scoreBig">
                {Math.round(ideation)}
              </p>
              <Progress
                value={Math.max(0, Math.min(100, Number(ideation)))}
                tone="primary"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="adminVentureDetail-scorePanel">
      <p className="adminVentureDetail-label">{label}</p>
      <p className="adminVentureDetail-scoreBig">{Math.round(value)}</p>
      <Progress value={Math.max(0, Math.min(100, value))} tone="primary" />
    </div>
  );
}

function ScoreBlock({ label, value, tone, icon: Icon }) {
  return (
    <div className="adminVentureDetail-scoreBlock">
      <span className="admin-muted adminVentureDetail-label">{label}</span>
      <div className="adminVentureDetail-scoreValueWrap">
        {Icon ? (
          <Icon
            size={15}
            className={
              tone === "success"
                ? "admin-text-success"
                : tone === "danger"
                  ? "admin-text-danger"
                  : "admin-muted"
            }
          />
        ) : null}
        <strong
          className={
            tone === "success"
              ? "admin-text-success"
              : tone === "danger"
                ? "admin-text-danger"
                : ""
          }
        >
          {value}
        </strong>
      </div>
    </div>
  );
}

function ProgressRow({ label, value, tone, scale = 100 }) {
  const numeric = Number(value ?? 0);
  const isNegative = numeric < 0;
  // For negatives, use the absolute value for bar width so the magnitude is
  // still visible, but force the danger tone and prefix the label with "-".
  const magnitude = Math.abs(numeric);
  const bounded = Math.max(0, Math.min(scale, magnitude));
  const progressValue = scale === 100 ? bounded : (bounded / scale) * 100;
  const effectiveTone = isNegative ? "danger" : tone;
  const displayMagnitude =
    scale === 100 ? Math.round(bounded) : bounded.toFixed(1);
  const signedDisplay = `${isNegative ? "-" : ""}${displayMagnitude}`;

  return (
    <div className="adminVentureDetail-progressRow">
      <div className="adminVentureDetail-progressHead">
        <span className="admin-muted adminVentureDetail-progressLabel">
          {label}
        </span>
        <strong className={isNegative ? "admin-text-danger" : ""}>
          {scale === 100 ? `${signedDisplay}%` : signedDisplay}
        </strong>
      </div>
      <Progress value={progressValue} tone={effectiveTone} />
    </div>
  );
}
